import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react"
import { ChevronDown } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Command,
  CommandGroup,
  CommandInput,
  CommandList,
  CommandItem,
} from "@f2e/ui/components/base"
import useDebounce from "@/hooks/useDebounce"
import {
  useGetExternalUserIdsApi,
  type ExternalUserIdentityOptionResponse,
} from "@/hooks/api"

export interface ExternalUserIdSelectProps {
  defaultValue?: string
  defaultClientUserId?: string
  onChange: (externalUserId: string, clientUserId?: string) => void
  compact?: boolean
  isLoading?: boolean
  debounceMs?: number
  minChars?: number
  currentWorkspaceId?: string
  currentOrganizationId?: string
  disableFreeTextSelection?: boolean
}

interface ExternalUserIdentityOption {
  client_user_id?: string
  label: string
}

const normalizeOptions = (data: unknown) => {
  if (!Array.isArray(data)) return []
  return (
    data as Array<ExternalUserIdentityOptionResponse | string>
  ).map<ExternalUserIdentityOption>((item) =>
    typeof item === "string" ? { label: item } : item
  )
}

const ExternalUserIdSelect: React.FC<ExternalUserIdSelectProps> = ({
  defaultValue = "",
  defaultClientUserId = "",
  onChange,
  compact = false,
  isLoading = false,
  debounceMs = 300,
  minChars = 3,
  currentWorkspaceId,
  currentOrganizationId,
  disableFreeTextSelection = false,
}) => {
  const { t } = useTranslation()
  const [value, setValue] = useState<string>(defaultValue)
  const [query, setQuery] = useState<string>("")
  const debounced = useDebounce(query, debounceMs)
  const [open, setOpen] = useState(false)
  const [externalUserOptions, setExternalUserOptions] = useState<
    ExternalUserIdentityOption[]
  >([])
  const inFlightRequestKeysRef = useRef<Set<string>>(new Set())
  const hydratedClientUserIdRef = useRef<string | null>(null)
  const scopeGenerationRef = useRef(0)
  const latestOptionsRequestKeyRef = useRef("")
  const latestHydrationRequestKeyRef = useRef("")
  const defaultClientUserIdRef = useRef(defaultClientUserId)
  const defaultValueRef = useRef(defaultValue)
  const translateRef = useRef(t)
  const [isAllSelected, setIsAllSelected] = useState(false)

  const { fetchFn: fetchExternalUserIds } = useGetExternalUserIdsApi()
  const scopeKey = `${currentWorkspaceId || ""}|${currentOrganizationId || ""}|${disableFreeTextSelection}`
  defaultClientUserIdRef.current = defaultClientUserId
  defaultValueRef.current = defaultValue
  translateRef.current = t

  const apply = () => {
    if (disableFreeTextSelection) return
    const trimmed = query.trim()
    onChange(trimmed)
    setValue(trimmed)
  }

  useLayoutEffect(() => {
    scopeGenerationRef.current += 1
    latestOptionsRequestKeyRef.current = ""
    latestHydrationRequestKeyRef.current = ""
    hydratedClientUserIdRef.current = null
    setExternalUserOptions([])
    setQuery("")

    if (defaultClientUserIdRef.current) {
      setValue("")
      setIsAllSelected(false)
    } else if (defaultValueRef.current === "all") {
      setValue(translateRef.current("historyList.externalUserId.all"))
      setIsAllSelected(true)
    } else {
      setValue(
        defaultValueRef.current ||
          translateRef.current("historyList.externalUserId.none")
      )
      setIsAllSelected(false)
    }
  }, [scopeKey])

  useEffect(() => {
    if (defaultClientUserId) {
      if (hydratedClientUserIdRef.current !== defaultClientUserId) {
        setValue("")
      }
      setIsAllSelected(false)
      return
    }
    hydratedClientUserIdRef.current = null
    if (defaultValue === "all") {
      setValue(t("historyList.externalUserId.all"))
      setIsAllSelected(true)
      return
    }
    if (!defaultValue) {
      setValue(t("historyList.externalUserId.none"))
    } else {
      setValue(defaultValue)
    }
    setIsAllSelected(false)
  }, [defaultClientUserId, defaultValue, t])

  const fetchIds = useCallback(
    async (q?: string, clientUserId?: string) => {
      const qTrimmed = q?.trim() || ""
      const scopeGeneration = scopeGenerationRef.current
      const key = `${scopeGeneration}|${currentWorkspaceId || ""}|${currentOrganizationId || ""}|${qTrimmed}|${clientUserId || ""}`
      const isHydrationRequest = !!clientUserId
      const latestRequestKeyRef = isHydrationRequest
        ? latestHydrationRequestKeyRef
        : latestOptionsRequestKeyRef
      // Record what the UI currently wants even when an identical request is
      // already in flight. Its response then becomes current again after a
      // q1 -> q2 -> q1 sequence.
      latestRequestKeyRef.current = key
      if (inFlightRequestKeysRef.current.has(key)) return
      inFlightRequestKeysRef.current.add(key)
      const params = new URLSearchParams()
      if (currentWorkspaceId) params.set("workspace_id", currentWorkspaceId)
      else if (currentOrganizationId)
        params.set("organization_id", currentOrganizationId)
      if (!disableFreeTextSelection && qTrimmed) params.set("q", qTrimmed)
      if (clientUserId) params.set("client_user_id", clientUserId)
      params.set("structured", "true")
      try {
        const response = await fetchExternalUserIds({ searchParams: params })
        if (
          scopeGeneration !== scopeGenerationRef.current ||
          latestRequestKeyRef.current !== key
        )
          return

        const options = normalizeOptions(response?.data)
        if (isHydrationRequest) {
          if (clientUserId !== defaultClientUserIdRef.current) return
          const selectedOption = options.find(
            (option) => option.client_user_id === clientUserId
          )
          if (!selectedOption) return

          setExternalUserOptions((currentOptions) =>
            currentOptions.some(
              (option) =>
                option.client_user_id === selectedOption.client_user_id
            )
              ? currentOptions
              : [selectedOption, ...currentOptions]
          )
          hydratedClientUserIdRef.current = clientUserId
          setValue(selectedOption.label)
          setIsAllSelected(false)
          return
        }

        const selectedOption = options.find(
          (option) => option.client_user_id === defaultClientUserIdRef.current
        )
        setExternalUserOptions((currentOptions) => {
          const hydratedOption = currentOptions.find(
            (option) => option.client_user_id === defaultClientUserIdRef.current
          )
          if (
            !hydratedOption ||
            options.some(
              (option) =>
                option.client_user_id === hydratedOption.client_user_id
            )
          )
            return options
          return [hydratedOption, ...options]
        })
        if (selectedOption) {
          hydratedClientUserIdRef.current =
            selectedOption.client_user_id || null
          setValue(selectedOption.label)
          setIsAllSelected(false)
        }
      } catch {
        // The hook exposes request errors separately; reopening retries here.
      } finally {
        inFlightRequestKeysRef.current.delete(key)
      }
    },
    [
      currentWorkspaceId,
      currentOrganizationId,
      disableFreeTextSelection,
      fetchExternalUserIds,
    ]
  )

  useEffect(() => {
    if (!defaultClientUserId) return
    fetchIds("", defaultClientUserId)
  }, [defaultClientUserId, fetchIds])

  useEffect(() => {
    const q = debounced.trim()
    if (disableFreeTextSelection) return
    if (!q || q.length < minChars || isAllSelected) return
    fetchIds(q)
  }, [debounced, minChars, isAllSelected, disableFreeTextSelection, fetchIds])

  useEffect(() => {
    if (open) {
      setQuery("")
      fetchIds("")
      if (
        defaultClientUserId &&
        hydratedClientUserIdRef.current !== defaultClientUserId
      ) {
        fetchIds("", defaultClientUserId)
      }
    }
  }, [defaultClientUserId, open, fetchIds])

  return (
    <div
      className={compact ? "grid gap-1.5" : "mb-4 flex items-center space-x-4"}
    >
      <label
        htmlFor="external-user-id-filter"
        className={
          compact ? "text-xs font-medium text-muted-foreground" : "font-medium"
        }
      >
        {t("historyList.externalUserId.label")}
      </label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className={compact ? "relative w-full" : "relative w-[240px]"}>
            <Input
              id="external-user-id-filter"
              className={compact ? "h-9 pr-8 text-sm" : "pr-8 text-sm"}
              placeholder={t("historyList.externalUserId.none")}
              value={value}
              readOnly={disableFreeTextSelection}
              onChange={(e) => {
                if (disableFreeTextSelection) return
                setIsAllSelected(false)
                setQuery(e.target.value)
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !disableFreeTextSelection) apply()
              }}
              disabled={isLoading}
            />
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50 cursor-pointer" />
          </div>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          side="bottom"
          className={
            compact
              ? "w-[var(--radix-popover-trigger-width)] p-0"
              : "w-[240px] p-0"
          }
        >
          <Command>
            <CommandInput
              placeholder={t(
                disableFreeTextSelection
                  ? "historyList.externalUserId.maskedPlaceholder"
                  : "historyList.externalUserId.placeholder"
              )}
              value={query}
              onValueChange={setQuery}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  if (disableFreeTextSelection) return
                  if (externalUserOptions.length > 0) {
                    const first = externalUserOptions[0]
                    setValue(first.label)
                    onChange(first.label, first.client_user_id || undefined)
                    setOpen(false)
                  } else {
                    apply()
                    setOpen(false)
                  }
                }
              }}
            />
            <CommandList>
              <CommandGroup>
                <CommandItem
                  key="none"
                  value="none"
                  onSelect={() => {
                    setValue(t("historyList.externalUserId.none"))
                    setQuery("")
                    setIsAllSelected(false)
                    onChange("")
                    setOpen(false)
                  }}
                >
                  {t("historyList.externalUserId.none")}
                </CommandItem>
                <CommandItem
                  key="all"
                  value="all"
                  onSelect={() => {
                    setValue(t("historyList.externalUserId.all"))
                    setQuery("")
                    setIsAllSelected(true)
                    onChange("all")
                    setOpen(false)
                  }}
                >
                  {t("historyList.externalUserId.all")}
                </CommandItem>
                {externalUserOptions.map((option) => (
                  <CommandItem
                    key={`${option.client_user_id || "legacy"}:${option.label}`}
                    value={option.label}
                    onSelect={() => {
                      setValue(option.label)
                      onChange(option.label, option.client_user_id || undefined)
                      setOpen(false)
                    }}
                  >
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}

export default React.memo(ExternalUserIdSelect)
